import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import '@operato/input/ox-input-select-buttons.js'

import { p13n } from '@operato/p13n'

import './data-ooc-view.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'

import { GhostPrint } from '@operato/ghost-print'

function getOocStatus() {
  return [
    {
      display: i18next.t('label.state-issued'),
      value: 'ISSUED'
    },
    {
      display: i18next.t('label.state-reviewed'),
      value: 'REVIEWED'
    },
    {
      display: i18next.t('label.state-corrected'),
      value: 'CORRECTED'
    }
  ]
}

import { DataGrist, FetchOption, GristRecord } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { openPopup } from '@operato/layout'
import { PageView, store } from '@operato/shell'
import { CommonGristStyles, CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice, adjustFilters } from '@operato/utils'

@customElement('data-ooc-list-page')
export class DataOocListPage extends connect(store)(p13n(localize(i18next)(PageView))) {
  static styles = [
    ScrollbarStyles,
    CommonGristStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;

        --grid-header-padding: 2px 0 2px 9px;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() private gristConfig: any
  @state() private mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'
  @state() private state?: string[]

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-ooc list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-ooc',
      actions: [
        {
          title: i18next.t('button.print'),
          action: this.printOocs.bind(this),
          icon: 'print'
        }
      ],
      exportable: {
        name: i18next.t('title.data-ooc list'),
        data: this._exportableData.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist
        .mode=${mode}
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
        .personalConfigProvider=${this.getPagePreferenceProvider('ox-grist')!}
      >
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-input-select-buttons
              .value=${this.state}
              .options=${getOocStatus()}
              @change=${(e: CustomEvent) => {
                this.state = (e.currentTarget as any)?.value
                this.grist.fetch()
              }}
            ></ox-input-select-buttons>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>

        <ox-grist-personalizer slot="setting"></ox-grist-personalizer>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    this.gristConfig = {
      list: {
        fields: ['dataSet', 'data', 'correctiveAction', 'corrector', 'correctedAt', 'collectedAt', 'creator']
      },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        { type: 'gutter', gutterName: 'row-selector', multiple: true, fixed: true },
        {
          type: 'gutter',
          gutterName: 'button',
          fixed: true,
          icon: 'assignment_turned_in',
          iconOnly: false,
          title: i18next.t('button.detail'),
          width: 72
        },
        {
          type: 'string',
          name: 'name',
          fixed: true,
          label: true,
          header: i18next.t('field.name'),
          record: {
            editable: false
          },
          filter: 'search',
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'description',
          label: true,
          header: i18next.t('field.description'),
          record: {
            editable: false
          },
          filter: 'search',
          width: 150,
          imex: true
        },
        {
          type: 'string',
          name: 'history',
          hidden: true,
          imex: true
        },
        {
          type: 'checkbox',
          name: 'ooc',
          header: i18next.t('field.ooc'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'checkbox',
          name: 'oos',
          header: i18next.t('field.oos'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'select',
          name: 'state',
          label: true,
          header: i18next.t('field.status'),
          record: {
            editable: false,
            options: [{ display: '', value: '' }, ...getOocStatus()]
          },
          width: 100,
          filter: true,
          imex: true
        },
        {
          type: 'string',
          name: 'correctiveInstruction',
          label: true,
          header: i18next.t('field.corrective-instruction'),
          record: {
            editable: false,
            language: 'text'
          },
          width: 150,
          imex: true
        },
        {
          type: 'string',
          name: 'correctiveAction',
          label: true,
          header: i18next.t('field.corrective-action'),
          record: {
            editable: false,
            language: 'text'
          },
          width: 150,
          imex: true
        },
        {
          type: 'resource-object',
          name: 'dataSet',
          header: i18next.t('field.data-set'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key01',
          header: {
            renderer: i18next.t('field.key-01'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key02',
          header: {
            renderer: i18next.t('field.key-02'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key03',
          header: {
            renderer: i18next.t('field.key-03'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key04',
          header: {
            renderer: i18next.t('field.key-04'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'string',
          name: 'key05',
          header: {
            renderer: i18next.t('field.key-05'),
            group: i18next.t('field.key')
          },
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'json5',
          name: 'partitionKeys',
          header: i18next.t('field.partition-keys'),
          record: {
            editable: false
          },
          width: 200,
          imex: true
        },
        {
          type: 'json5',
          name: 'data',
          header: i18next.t('field.data'),
          record: {
            editable: false
          },
          width: 200,
          imex: true
        },
        {
          type: 'text',
          name: 'rawData',
          header: i18next.t('field.raw-data'),
          record: {
            editable: false
          },
          width: 200,
          imex: true
        },
        {
          type: 'string',
          name: 'workDate',
          header: i18next.t('field.work-date'),
          sortable: true,
          width: 80,
          imex: true
        },
        {
          type: 'string',
          name: 'workShift',
          header: i18next.t('field.work-shift'),
          sortable: true,
          width: 40,
          imex: true
        },
        {
          type: 'datetime',
          name: 'collectedAt',
          header: i18next.t('field.collected-at'),
          sortable: true,
          width: 180,
          imex: true
        },
        {
          type: 'resource-object',
          name: 'updater',
          header: i18next.t('field.updater'),
          sortable: true,
          width: 120,
          imex: true
        },
        {
          type: 'datetime',
          name: 'updatedAt',
          header: i18next.t('field.updated_at'),
          sortable: true,
          width: 180,
          imex: true
        }
      ],
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        },
        handlers: {
          click: (columns, data, column, record, rowIndex) => {
            const popup = openPopup(
              html` <data-ooc-view data-ooc-id=${record.id} style="background-color: white;"></data-ooc-view> `,
              {
                backdrop: true,
                size: 'large',
                title: i18next.t('title.data-ooc view')
              }
            )
            popup.onclosed = () => {
              // this.grist.fetch()
            }
          }
        }
      },
      sorters: [
        {
          name: 'collectedAt',
          desc: true
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    if (this.state) {
      filters = adjustFilters(filters, [{ name: 'state', operator: 'eq', value: this.state }])
    }

    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!]) {
          responses: dataOocs(filters: $filters, pagination: $pagination, sortings: $sortings) {
            items {
              id
              name
              description
              useCase
              dataSet {
                id
                name
              }
              key01
              key02
              key03
              key04
              key05
              partitionKeys
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                type
                unit
                options
                quota
                spec
                stat
              }
              data
              rawData
              judgment
              ooc
              oos
              state
              history
              workDate
              workShift
              correctiveInstruction
              correctiveAction
              reviewedAt
              reviewer {
                id
                name
              }
              correctedAt
              corrector {
                id
                name
              }
              updater {
                id
                name
              }
              updatedAt
              collectedAt
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }

  printOocs() {
    const selected = this.grist.selected
    const ids = selected.map(record => record.id).join('|')

    GhostPrint.print({
      src: `./data-oocs?ids=${ids}`
      // pending: 200 * selected.length
    })
  }
}
